import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Modal,
  Header,
  Button,
  List,
  Icon,
  Segment,
  Table,
  Label,
  Popup,
} from "semantic-ui-react";
import { closeModal } from "../../../../app/common/modals/modalSlice";
import { changePageScaleFill } from "../../../../app/annots/annotSlice";
import { validateFormFields, groupIssuesByPage } from "./DocFillValidation";
import { getFormFieldData } from "./formFieldData/formFieldData";
import { addTaskToDb } from "../../../../app/firestore/firestoreService";
import { toast } from "react-toastify";

// State-based mapping of form field names to task names
// Each state can define which checkbox fields map to which task names
const CHECKLIST_TASK_MAPPING = {
  Colorado: {
    // Example mappings - add actual field names as needed
    // "Checkbox Field Name": "Task Name to Add",
    "Checkbox ChecklistItem Personal Property Agreement":
      "Verify Personal Property Agreement",
    "Checkbox ChecklistItem Solar Power Plan": "Verify Solar Power Plan",
    "Radiobutton WhoOrdersNew_ILC_OrSurvey Buyer": "Buyer Orders New ILC",
    "Radiobutton WhoOrdersNew_ILC_OrSurvey Seller": "Seller Orders New ILC",
    "Delivery ILC": "Deliver New ILC or Survey",
    "Occupancy Agreements": "Review Occupancy Agreements",
    "Radiobutton AcknowledgeReceiptWelloPermit Does Not":
      "Get Copy of Well Permit",
    "Checkbox ChecklistItem Post-Closing Occupancy Agreement":
      "Verify Post-Closing Occupancy Agreement",
    "Checkbox ChecklistItem Seller Association Authorization":
      "Verify Seller Association Authorization",
      "Checkbox ChecklistItem All contracts relating to the operation maintenance and management of the Property":
      "Other Documents: All contracts relating to the operation maintenance and management of the Property",
      "Checkbox ChecklistItem Property tax bills for the last number of years":
      "Other Documents: Property tax bills for the last number of years",
      "Checkbox ChecklistItem As built construction plans to the Property and the tenant improvements including architectural electrical mechanical and structural systems engineering reports permanent Certificates of Occupancy":
      "Other Documents: As built construction plans to the Property and the tenant improvements including architectural electrical mechanical and structural systems engineering reports permanent Certificates of Occupancy",
      "Checkbox ChecklistItem A list of all Inclusions to be conveyed to Buyer":
      "Other Documents: A list of all Inclusions to be conveyed to Buyer",
      "Checkbox ChecklistItem Operating statements for the past number of years":
      "Other Documents: Operating statements for the past number of years",
      "Checkbox ChecklistItem A rent roll accurate and correct to the date of this Contract":
      "Other Documents: A rent roll accurate and correct to the date of this Contract",
      "Checkbox ChecklistItem A schedule of any tenant improvement work Seller is obligated to complete but has not yet completed":
      "Other Documents: A schedule of any tenant improvement work Seller is obligated to complete but has not yet completed",
      "Checkbox ChecklistItem All insurance policies pertaining to the Property and copies of any claims which have been made for the past number of years":
      "Other Documents: All insurance policies pertaining to the Property and copies of any claims which have been made for the past number of years",
      "Checkbox ChecklistItem Soils reports surveys and engineering reports or data pertaining to the Property if not delivered earlier":
      "Other Documents: Soils reports surveys and engineering reports or data pertaining to the Property if not delivered earlier",
      "Checkbox ChecklistItem Any and all existing documentation and reports regarding Phase I and II environmental reports":
      "Other Documents: Any and all existing documentation and reports regarding Phase I and II environmental reports",
      "Checkbox ChecklistItem Any Americans with Disabilities Act reports studies or surveys concerning the compliance of the Property with said Act":
      "Other Documents: Any Americans with Disabilities Act reports studies or surveys concerning the compliance of the Property with said Act",
      "Checkbox ChecklistItem All permits licenses and other building or use authorizations issued by any governmental authority":
      "Other Documents: All permits licenses and other building or use authorizations issued by any governmental authority",
      "Checkbox ChecklistItem Other Documents": "Other Documents: Other Documents",
      "Radiobutton WhoProvidesPhaseI_EnvironmentalSiteAssessment Seller":
        "Seller Provides Phase I Environmental Site Assessment",
        "Radiobutton WhoProvidesPhaseI_EnvironmentalSiteAssessment Buyer":
        "Buyer Provides Phase I Environmental Site Assessment",
        "Radiobutton Rents Actually Received": "Seller Provides Rents Actually Received",
        "Radiobutton Rents Accrued": "Seller Provides Rents Accrued",
        "Radiobutton SellerFinancingDeliversFinancingDocuments Buyer":
        "Buyer Provides Seller Financing Documents",
        "Radiobutton SellerFinancingDeliversFinancingDocuments Seller":
        "Seller Provides Seller Financing Documents",
  },
  Oklahoma: {
    // Example mappings - add actual field names as needed
  },
  Texas: {
    // Example mappings - add actual field names as needed
  },
  California: {
    // Example mappings - add actual field names as needed
  },
  Louisiana: {
    // Example mappings - add actual field names as needed
  },
};

function ChecklistItemsSection({ doc, transaction, existingTasks }) {
  const [addedTasks, setAddedTasks] = useState({});
  const [addingTask, setAddingTask] = useState({});

  const formFieldValues = doc.formFieldValues || {};
  const state = transaction?.agentProfile?.state || "Colorado";

  // Get the mapping for the current state
  const stateMapping = CHECKLIST_TASK_MAPPING[state] || {};

  // Find all checkbox fields that are in the mapping and are checked
  const checklistItems = Object.entries(stateMapping)
    .filter(([formFieldName]) => {
      const fieldValue = formFieldValues[formFieldName];
      return fieldValue && fieldValue !== "" && fieldValue !== "Off";
    })
    .map(([formFieldName, taskName]) => ({
      formFieldName,
      taskName,
    }));

  if (checklistItems.length === 0) {
    return null;
  }

  // Check if task already exists in transaction
  const taskExists = (taskName) => {
    if (!existingTasks || existingTasks.length === 0) return false;
    return existingTasks.some(
      (task) => task.title?.toLowerCase() === taskName.toLowerCase()
    );
  };

  const handleAddTask = async (item) => {
    setAddingTask((prev) => ({ ...prev, [item.formFieldName]: true }));

    try {
      await addTaskToDb(
        {
          title: item.taskName,
          category: "Task",
          description: `Added from document checklist: ${doc.title}`,
          status: "Active",
          start: null,
          end: null,
          transactionId: transaction.id,
          transactionTitle: transaction.title,
        },
        transaction
      );
      setAddedTasks((prev) => ({ ...prev, [item.formFieldName]: true }));
      toast.success(`Task "${item.taskName}" added successfully`);
    } catch (error) {
      toast.error(`Failed to add task: ${error.message}`);
    } finally {
      setAddingTask((prev) => ({ ...prev, [item.formFieldName]: false }));
    }
  };

  return (
    <div style={{ overflowY: "auto", maxHeight: "250px" }}>
    <Table
      compact
      celled
      size="small"
      style={{ maxWidth: "450px", marginBottom: "0" }}
    >
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell colSpan={2}>
            <Icon name="tasks" color="blue" /> Checklist Items{" "}
            <Popup
              trigger={<Icon name="info circle" color="blue" />}
              position="top center"
            >
              <p className="bold text blue mini bottom margin">
                Checklist Items - Add task{" "}
              </p>
              <p>These items are checkboxes that were selected, and can add a task to ensure they get addressed.</p>
              <p>
                <b>Add Task</b> button will add to your ToDo items in the Tasks
                Section of this transaction. <br />
                You can edit the name or add a date for this task in the Tasks
                Section. <br />
                <i>
                  Note: if you change the name of this task, it will show up
                  again here with "Add Task" button instead of "Task already
                  added" as the name will no longer match the field being
                  searched.
                </i>
              </p>
            </Popup>
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {checklistItems.map((item) => {
          const alreadyExists = taskExists(item.taskName);
          const justAdded = addedTasks[item.formFieldName];
          const isAdding = addingTask[item.formFieldName];

          return (
            <Table.Row key={item.formFieldName}>
              <Table.Cell width={10}>
                <Icon name="check square outline" color="green" />{" "}
                {item.taskName}
              </Table.Cell>
              <Table.Cell width={6} textAlign="center">
                {alreadyExists || justAdded ? (
                  <Label color="grey" size="small">
                    <Icon name="check" /> Task already added
                  </Label>
                ) : (
                  <Button
                    size="mini"
                    color="blue"
                    onClick={() => handleAddTask(item)}
                    loading={isAdding}
                    disabled={isAdding}
                  >
                    <Icon name="plus" /> Add Task
                  </Button>
                )}
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
    </div>
  );
}

function ValidationSummaryTable({
  validationIssues,
  doc,
  transaction,
  scrollToField,
}) {
  // Count radiobuttons none selected
  const radiobuttonsNoneSelected = validationIssues.filter(
    (issue) => issue.type === "radiogroup" && issue.message === "none selected"
  ).length;

  // Count radiobuttons more than 1 selected
  const radiobuttonsMultipleSelected = validationIssues.filter(
    (issue) =>
      issue.type === "radiogroup" &&
      issue.message === "multiple selected (only one allowed)"
  ).length;

  // Count text fields blank (including textarea)
  const textFieldsBlank = validationIssues.filter(
    (issue) =>
      (issue.type === "text" || issue.type === "textarea") &&
      issue.message === "field is blank"
  ).length;

  // Check agent signature status
  const formFieldData = getFormFieldData(doc.title, transaction) || [];
  const formFieldValues = doc.formFieldValues || {};
  const agentRepresents = transaction?.agentRepresents;
  const hasCoAgent = transaction?.hasCoAgent;

  let agentSignatureStatus = null;
  let coAgentSignatureStatus = null;

  if (agentRepresents === "Buyer") {
    // Check if "Signature Buyer Broker" field exists in form
    const signatureField = formFieldData.find(
      (f) => f.name === "Signature Buyer Broker"
    );
    const dateField = formFieldData.find(
      (f) => f.name === "Date Signature Buyer Broker"
    );

    if (signatureField) {
      const signatureValue = formFieldValues["Signature Buyer Broker"];
      const dateValue = formFieldValues["Date Signature Buyer Broker"];
      const isSigned = signatureValue && signatureValue.trim() !== "";
      const hasDate = dateValue && dateValue !== "";

      if (!isSigned || !hasDate) {
        agentSignatureStatus = {
          role: "Buyer Broker",
          isSigned,
          hasDate,
          signatureField,
          dateField,
        };
      }
    }

    // Check CoAgent signature if hasCoAgent
    if (hasCoAgent) {
      const coAgentSignatureField = formFieldData.find(
        (f) => f.name === "Signature Buyer CoAgent"
      );
      const coAgentDateField = formFieldData.find(
        (f) => f.name === "Date Signature Buyer CoAgent"
      );

      if (coAgentSignatureField) {
        const coAgentSignatureValue =
          formFieldValues["Signature Buyer CoAgent"];
        const coAgentDateValue =
          formFieldValues["Date Signature Buyer CoAgent"];
        const coAgentIsSigned =
          coAgentSignatureValue && coAgentSignatureValue.trim() !== "";
        const coAgentHasDate = coAgentDateValue && coAgentDateValue !== "";

        if (!coAgentIsSigned || !coAgentHasDate) {
          coAgentSignatureStatus = {
            role: "Buyer CoAgent",
            isSigned: coAgentIsSigned,
            hasDate: coAgentHasDate,
            signatureField: coAgentSignatureField,
            dateField: coAgentDateField,
          };
        }
      }
    }
  } else if (agentRepresents === "Seller") {
    // Check if "Signature Seller Broker" field exists in form
    const signatureField = formFieldData.find(
      (f) => f.name === "Signature Seller Broker"
    );
    const dateField = formFieldData.find(
      (f) => f.name === "Date Signature Seller Broker"
    );

    if (signatureField) {
      const signatureValue = formFieldValues["Signature Seller Broker"];
      const dateValue = formFieldValues["Date Signature Seller Broker"];
      const isSigned = signatureValue && signatureValue.trim() !== "";
      const hasDate = dateValue && dateValue !== "";

      if (!isSigned || !hasDate) {
        agentSignatureStatus = {
          role: "Seller Broker",
          isSigned,
          hasDate,
          signatureField,
          dateField,
        };
      }
    }

    // Check CoAgent signature if hasCoAgent
    if (hasCoAgent) {
      const coAgentSignatureField = formFieldData.find(
        (f) => f.name === "Signature Seller CoAgent"
      );
      const coAgentDateField = formFieldData.find(
        (f) => f.name === "Date Signature Seller CoAgent"
      );

      if (coAgentSignatureField) {
        const coAgentSignatureValue =
          formFieldValues["Signature Seller CoAgent"];
        const coAgentDateValue =
          formFieldValues["Date Signature Seller CoAgent"];
        const coAgentIsSigned =
          coAgentSignatureValue && coAgentSignatureValue.trim() !== "";
        const coAgentHasDate = coAgentDateValue && coAgentDateValue !== "";

        if (!coAgentIsSigned || !coAgentHasDate) {
          coAgentSignatureStatus = {
            role: "Seller CoAgent",
            isSigned: coAgentIsSigned,
            hasDate: coAgentHasDate,
            signatureField: coAgentSignatureField,
            dateField: coAgentDateField,
          };
        }
      }
    }
  }

  return (
    <div>
    <Table
      compact
      celled
      size="small"
      style={{ maxWidth: "480px", marginBottom: "0" }}
    >
      <Table.Body>
        <Table.Row>
          <Table.HeaderCell colSpan={2} style={{ backgroundColor: "#f9fafb", padding: "10px" }}>
            <Icon name="warning" color="red" /> Potential Issues
          </Table.HeaderCell>
        </Table.Row>
        <Table.Row>
          <Table.HeaderCell style={{ textAlign: "center" }}>#</Table.HeaderCell>
          <Table.HeaderCell>Issue</Table.HeaderCell>
        </Table.Row>
        <Table.Row>
          <Table.Cell width={1} textAlign="center">
            {radiobuttonsNoneSelected}
          </Table.Cell>
          <Table.Cell width={6}>
            <Icon name="dot circle outline" color="orange" /> Radiobutton Groups
            - None Selected
          </Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell textAlign="center">
            {radiobuttonsMultipleSelected}
          </Table.Cell>
          <Table.Cell>
            <Icon name="dot circle outline" color="red" /> Radiobutton Groups -
            More Than 1 Selected
          </Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell textAlign="center">{textFieldsBlank}</Table.Cell>
          <Table.Cell>
            <Icon name="edit outline" color="red" /> Text Fields Blank
          </Table.Cell>
        </Table.Row>
        {agentSignatureStatus && (
          <Table.Row warning>
            <Table.Cell textAlign="center">
              <Icon
                name="pencil"
                color="yellow"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  scrollToField(
                    !agentSignatureStatus.isSigned
                      ? agentSignatureStatus.signatureField
                      : agentSignatureStatus.dateField
                  )
                }
              />
            </Table.Cell>
            <Table.Cell>
              Agent Has Not Signed ({agentSignatureStatus.role})
              <br />
              {!agentSignatureStatus.isSigned && !agentSignatureStatus.hasDate
                ? "Missing signature & date"
                : !agentSignatureStatus.isSigned
                ? "Missing signature"
                : "Missing date"}
            </Table.Cell>
          </Table.Row>
        )}
        {coAgentSignatureStatus && (
          <Table.Row warning>
            <Table.Cell textAlign="center">
              <Icon
                name="pencil"
                color="yellow"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  scrollToField(
                    !coAgentSignatureStatus.isSigned
                      ? coAgentSignatureStatus.signatureField
                      : coAgentSignatureStatus.dateField
                  )
                }
              />
            </Table.Cell>
            <Table.Cell>
              CoAgent Has Not Signed ({coAgentSignatureStatus.role})
              <br />
              {!coAgentSignatureStatus.isSigned &&
              !coAgentSignatureStatus.hasDate
                ? "Missing signature & date"
                : !coAgentSignatureStatus.isSigned
                ? "Missing signature"
                : "Missing date"}
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table>
    </div>
  );
}

export default function DocFillValidationModal() {
  const dispatch = useDispatch();
  const { doc } = useSelector((state) => state.doc);
  const { transaction } = useSelector((state) => state.transaction);
  const {
    tasksTransWithDateAll,
    tasksTransWithoutDateActive,
    tasksTransWithoutDateComplete,
  } = useSelector((state) => state.task);
  const [validationIssues, setValidationIssues] = useState([]);
  const [groupedIssues, setGroupedIssues] = useState({});

  // Combine all tasks for checking if task already exists
  const existingTasks = [
    ...(tasksTransWithDateAll || []),
    ...(tasksTransWithoutDateActive || []),
    ...(tasksTransWithoutDateComplete || []),
  ];

  useEffect(() => {
    const issues = validateFormFields(doc, transaction);

    // Add agent signature issues
    const formFieldData = getFormFieldData(doc.title, transaction) || [];
    const formFieldValues = doc.formFieldValues || {};
    const agentRepresents = transaction?.agentRepresents;
    const hasCoAgent = transaction?.hasCoAgent;

    if (agentRepresents === "Buyer") {
      const signatureField = formFieldData.find(
        (f) => f.name === "Signature Buyer Broker"
      );
      const dateField = formFieldData.find(
        (f) => f.name === "Date Signature Buyer Broker"
      );

      if (signatureField) {
        const signatureValue = formFieldValues["Signature Buyer Broker"];
        const dateValue = formFieldValues["Date Signature Buyer Broker"];
        const isSigned = signatureValue && signatureValue.trim() !== "";
        const hasDate = dateValue && dateValue !== "";

        if (!isSigned) {
          issues.push({
            type: "agentSignature",
            message: "agent has not signed",
            field: signatureField,
          });
        }
        if (!hasDate && dateField) {
          issues.push({
            type: "agentSignature",
            message: "agent signature date is missing",
            field: dateField,
          });
        }
      }

      // Add CoAgent signature issues if hasCoAgent
      if (hasCoAgent) {
        const coAgentSignatureField = formFieldData.find(
          (f) => f.name === "Signature Buyer CoAgent"
        );
        const coAgentDateField = formFieldData.find(
          (f) => f.name === "Date Signature Buyer CoAgent"
        );

        if (coAgentSignatureField) {
          const coAgentSignatureValue =
            formFieldValues["Signature Buyer CoAgent"];
          const coAgentDateValue =
            formFieldValues["Date Signature Buyer CoAgent"];
          const coAgentIsSigned =
            coAgentSignatureValue && coAgentSignatureValue.trim() !== "";
          const coAgentHasDate = coAgentDateValue && coAgentDateValue !== "";

          if (!coAgentIsSigned) {
            issues.push({
              type: "coAgentSignature",
              message: "coagent has not signed",
              field: coAgentSignatureField,
            });
          }
          if (!coAgentHasDate && coAgentDateField) {
            issues.push({
              type: "coAgentSignature",
              message: "coagent signature date is missing",
              field: coAgentDateField,
            });
          }
        }
      }
    } else if (agentRepresents === "Seller") {
      const signatureField = formFieldData.find(
        (f) => f.name === "Signature Seller Broker"
      );
      const dateField = formFieldData.find(
        (f) => f.name === "Date Signature Seller Broker"
      );

      if (signatureField) {
        const signatureValue = formFieldValues["Signature Seller Broker"];
        const dateValue = formFieldValues["Date Signature Seller Broker"];
        const isSigned = signatureValue && signatureValue.trim() !== "";
        const hasDate = dateValue && dateValue !== "";

        if (!isSigned) {
          issues.push({
            type: "agentSignature",
            message: "agent has not signed",
            field: signatureField,
          });
        }
        if (!hasDate && dateField) {
          issues.push({
            type: "agentSignature",
            message: "agent signature date is missing",
            field: dateField,
          });
        }
      }

      // Add CoAgent signature issues if hasCoAgent
      if (hasCoAgent) {
        const coAgentSignatureField = formFieldData.find(
          (f) => f.name === "Signature Seller CoAgent"
        );
        const coAgentDateField = formFieldData.find(
          (f) => f.name === "Date Signature Seller CoAgent"
        );

        if (coAgentSignatureField) {
          const coAgentSignatureValue =
            formFieldValues["Signature Seller CoAgent"];
          const coAgentDateValue =
            formFieldValues["Date Signature Seller CoAgent"];
          const coAgentIsSigned =
            coAgentSignatureValue && coAgentSignatureValue.trim() !== "";
          const coAgentHasDate = coAgentDateValue && coAgentDateValue !== "";

          if (!coAgentIsSigned) {
            issues.push({
              type: "coAgentSignature",
              message: "coagent has not signed",
              field: coAgentSignatureField,
            });
          }
          if (!coAgentHasDate && coAgentDateField) {
            issues.push({
              type: "coAgentSignature",
              message: "coagent signature date is missing",
              field: coAgentDateField,
            });
          }
        }
      }
    }

    setValidationIssues(issues);
    setGroupedIssues(groupIssuesByPage(issues));
  }, [doc, transaction]);

  function handleClose() {
    // Hide validation arrows when closing modal
    window.dispatchEvent(
      new CustomEvent("docFillValidation", {
        detail: { action: "hideValidation" },
      })
    );
    dispatch(closeModal());
  }

  function handleShowOnDocument() {
    // Show validation arrows on document
    window.dispatchEvent(
      new CustomEvent("docFillValidation", {
        detail: { action: "showValidation" },
      })
    );
    dispatch(closeModal());
  }

  function scrollToField(field) {
    // Set zoom to 120%
    dispatch(changePageScaleFill(1.2));

    // Show validation arrows for this specific field with orange highlighting
    window.dispatchEvent(
      new CustomEvent("docFillValidation", {
        detail: {
          action: "highlightSingleField",
          field: field,
          highlight: true,
        },
      })
    );

    // Close modal first
    dispatch(closeModal());

    // Simple scroll to page approach (with slightly longer delay to allow zoom to apply)
    setTimeout(() => {
      const pageElement = document.getElementById(`page-${field.page}`);
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 150);
  }

  const renderIssueIcon = (issue) => {
    switch (issue.type) {
      case "radiogroup":
        return <Icon name="dot circle outline" color="orange" />;
      case "text":
      case "textarea":
        return <Icon name="edit outline" color="red" />;
      case "agentSignature":
      case "coAgentSignature":
        return <Icon name="pencil" color="yellow" />;
      default:
        return <Icon name="warning" color="yellow" />;
    }
  };

  const renderIssueDescription = (issue) => {
    switch (issue.type) {
      case "radiogroup":
        return `Radio button group "${issue.groupName}": ${issue.message}`;
      case "text":
        return `Text field "${issue.field.name}": ${issue.message}`;
      case "textarea":
        return `Text area "${issue.field.name}": ${issue.message}`;
      case "agentSignature":
        return `Agent signature "${issue.field.name}": ${issue.message}`;
      case "coAgentSignature":
        return `CoAgent signature "${issue.field.name}": ${issue.message}`;
      default:
        return `Field "${issue.field.name}": ${issue.message}`;
    }
  };

  return (
    <Modal open={true} onClose={handleClose} size="large">
      <Header icon="check circle" content="Form Validation Results" />
      <Modal.Content
        style={{
          maxHeight: "60vh",
          overflowY: "auto",
          paddingBottom: "10px",
        }}
      >
        {validationIssues.length === 0 ? (
          <Segment textAlign="center" color="green">
            <Icon name="check circle" size="huge" color="green" />
            <Header as="h3" color="green">
              All fields are filled!
            </Header>
            <p>No validation issues found in this form.</p>
          </Segment>
        ) : (
          <>
            <Header as="h4" color="red">
              <Icon name="warning" />
              Found {validationIssues.length} potential issue
              {validationIssues.length !== 1 ? "s" : ""}
            </Header>
            <p>
              <i>
                Note that some of these may not be actual errors, and not all
                issues are found and displayed.
              </i>
            </p>
            {/* Summary Table and Checklist Items side by side */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "20px",
                alignItems: "flex-start",
                marginBottom: "15px",
              }}
            >
              <ValidationSummaryTable
                validationIssues={validationIssues}
                doc={doc}
                transaction={transaction}
                scrollToField={scrollToField}
              />
              <ChecklistItemsSection
                doc={doc}
                transaction={transaction}
                existingTasks={existingTasks}
              />
            </div>

            {Object.entries(groupedIssues).map(([pageNum, issues]) => (
              <Segment key={pageNum}>
                <Header as="h5">Page {parseInt(pageNum) + 1}</Header>
                <List divided relaxed>
                  {issues.map((issue, index) => (
                    <List.Item key={index}>
                      <List.Content>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <Button
                            size="mini"
                            primary
                            onClick={() => scrollToField(issue.field)}
                          >
                            Go to field
                          </Button>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                            }}
                          >
                            {renderIssueIcon(issue)}
                            <span>{renderIssueDescription(issue)}</span>
                          </div>
                        </div>
                      </List.Content>
                    </List.Item>
                  ))}
                </List>
              </Segment>
            ))}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        {validationIssues.length > 0 && (
          <Button onClick={handleShowOnDocument} color="orange">
            <Icon name="eye" /> Show on Document
          </Button>
        )}
        <Button onClick={handleClose} primary>
          <Icon name="check" /> Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
